import {
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import {
  AccountStatus,
  AccountSubType,
  AccountType,
  AccountUsage
} from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import React, { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import * as Yup from "yup";
import { INVOICE_ACCOUNT_NAME } from "../../Invoices/utils";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { QUERY_PAYMENTS_STATUS } from "../../query/payments/keys";
import { useBankInstitution } from "../../query/payments/queries";
import { QUERY_USERS_ACCOUNTS } from "../../query/users/keys";
import { paymentsService } from "../../services/payments";
import { usersService } from "../../services/users";
import { validatorRoutingNumber } from "../../utils/validators";
import { WSErrorMessage } from "../WSErrorMessage/WSErrorMessage";
import previewStyles from "./ManualAccountFormPreview.module.scss";

export const ManualAccountFormPreview: React.FC<{
  routingNumberField: string;
  accountNameField: string;
} & WSElementProps> = ({ routingNumberField, accountNameField, ...props }) => {
  const { watch, setError, clearErrors, setValue, errors } = useFormContext();

  const institutionNumber = watch(routingNumberField);

  useEffect(() => {
    if (institutionNumber?.length !== 9) {
      setValue(accountNameField, "");
    }
  }, [institutionNumber]);

  const institutionQuery = useBankInstitution(institutionNumber, {
    enabled: institutionNumber?.length === 9,
    onError() {
      setError(routingNumberField, {
        type: "setValueAs",
        message: "We couldn't find a bank with this routing number."
      });
      setValue(accountNameField, "");
    },
    onSuccess(data) {
      setValue(accountNameField, data.name);
      clearErrors(routingNumberField);
    }
  });

  if (institutionNumber?.length !== 9) {
    return <WSElement {...props} />;
  }

  if (institutionQuery.data) {
    return (
      <WSFlexBox.CenterY {...props}>
        <WSIcon name="institution" size="S" color="grey500" mr="XS" />
        <WSText.Heading6 color="grey500">
          {institutionQuery.data.name}
        </WSText.Heading6>
      </WSFlexBox.CenterY>
    );
  } else if (institutionQuery.error && !errors[routingNumberField]) {
    return (
      <WSFlexBox.CenterY mb="S">
        <WSIcon name="warning" size="XS" color="red400" mr="S" />
        <WSText.LabelSmallBold>
          We couldn't find a bank with this routing number.
        </WSText.LabelSmallBold>
      </WSFlexBox.CenterY>
    );
  } else {
    return <WSElement {...props} />;
  }
};

type BankAccountManualFormValues = {
  type: "checking" | "savigns";
  accountNumber: string;
  accountName: string;
  accountRoutingNumber: string;
  accountHolderName: string;
};

export const ManualAccountForm: React.FC<{
  onSubmit(): void;
  submitButtonText: string;
  submitButtonName: string;
  payoutPreferencesType?: "standard" | "instant";
  error?: any;
}> = ({
  submitButtonName,
  onSubmit,
  submitButtonText,
  error,
  payoutPreferencesType = "standard"
}) => {
  const refPreview = useRef<HTMLDivElement>(null);
  const userId = useUserId();

  const [submit, submitMeta] = useWSMutation<
    any,
    AxiosError,
    BankAccountManualFormValues
  >(
    async values => {
      const accounts = await usersService.account.create({
        name: INVOICE_ACCOUNT_NAME,
        status: AccountStatus.Active,
        type: AccountType.Depository,
        subType:
          values.type === "checking"
            ? AccountSubType.Checking
            : AccountSubType.Savings,
        numbers: {
          account: values.accountNumber,
          routing: values.accountRoutingNumber,
          routingWire: values.accountRoutingNumber
        },
        usage: AccountUsage.Mixed,
        owners: [values.accountHolderName]
      });

      const accountId = Array.isArray(accounts)
        ? accounts[0].accountId
        : accounts.accountId;

      return await paymentsService.payoutSettings.update(userId, {
        // payoutPreferences: PayoutPreferences.Standard,
        [payoutPreferencesType]: {
          destinationType: PayoutDestinationType.Account,
          destinationId: accountId
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACCOUNTS, QUERY_PAYMENTS_STATUS],
      onSuccess: onSubmit
    }
  );

  return (
    <>
      <WSElement
        ref={refPreview}
        className={previewStyles.image}
        data-focus="init"
        mb="L"
      />
      <WSForm<BankAccountManualFormValues>
        onSubmit={submit}
        defaultValues={{
          type: "checking",
          accountRoutingNumber: "",
          accountName: "",
          accountNumber: "",
          accountHolderName: ""
        }}
        validationSchema={Yup.object().shape({
          accountRoutingNumber: validatorRoutingNumber,
          accountName: Yup.string().required("Account name is required"),
          accountNumber: Yup.string().required("Account number is required"),
          accountHolderName: Yup.string().required("Account holder is required")
        })}
      >
        <WSForm.Field
          label="Account type"
          mb="M"
          name="type"
          component={WSSelect}
          componentProps={{
            options: [
              { value: "checking", label: "Checking" },
              { value: "savings", label: "Savings" }
            ]
          }}
        />
        <WSForm.Field
          name="accountRoutingNumber"
          label="Routing number"
          component={WSTextInput}
          componentProps={{
            formatOptions: {
              numericOnly: true,
              blocks: [9]
            },
            fsExclude: true,
            onFocus() {
              refPreview.current?.setAttribute("data-focus", "routing");
            }
          }}
          mb="S"
        />

        <ManualAccountFormPreview
          mb="S"
          routingNumberField="accountRoutingNumber"
          accountNameField="accountName"
        />

        <WSForm.Field name="accountName" component={WSTextInput} hidden />

        <WSForm.Field
          name="accountNumber"
          label="Account number"
          component={WSTextInput}
          componentProps={{
            formatOptions: {
              numericOnly: true,
              blocks: [30]
            },
            fsExclude: true,
            onFocus() {
              refPreview.current?.setAttribute("data-focus", "account");
            }
          }}
          mb="M"
        />

        <WSForm.Field
          name="accountHolderName"
          label="Name of account holder"
          component={WSTextInput}
          componentProps={{
            fsExclude: true,
            onFocus() {
              refPreview.current?.setAttribute("data-focus", "success");
            }
          }}
          mb="M"
        />

        <WSErrorMessage
          mb="M"
          contextKey="PaymentsSetup"
          error={error || submitMeta.error}
        />

        <WSForm.Values
          names={["accountName", "accountNumber", "accountHolderName"]}
        >
          {values => {
            const isValid = Object.values(values).every(Boolean);

            return (
              <WSForm.SubmitButton
                disabled={!isValid}
                kind={isValid ? "Primary" : "Disabled"}
                type="submit"
                fullWidth
                name={submitButtonName}
              >
                {submitButtonText}
              </WSForm.SubmitButton>
            );
          }}
        </WSForm.Values>
      </WSForm>
    </>
  );
};
